import { useTranslation } from "react-i18next";
import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * RiskTag — squared mono risk-level chip (Wysokie / Średnie / Niskie) with a
 * status dot: the house way to label greenwashing risk (replaces the old
 * RiskMeter bar). Colours come from the risk soft/line/ink token pairs so the
 * chip re-themes with the token scale; the default label is translated from
 * `results:riskLevels.*` (override with `label` for a custom string). For a
 * numeric score use ScoreRing instead.
 */
const riskTagVariants = cva(
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--r-xs)] border font-mono font-semibold uppercase tracking-[var(--tracking-tag)]",
  {
    defaultVariants: { level: "high", size: "md" },
    variants: {
      level: {
        high:
          "border-[var(--risk-high-line)] bg-[var(--risk-high-soft)] text-[var(--risk-high)]",
        med:
          "border-[var(--risk-med-line)] bg-[var(--risk-med-soft)] text-[var(--risk-med-ink)]",
        low:
          "border-[var(--risk-low-line)] bg-[var(--risk-low-soft)] text-[var(--risk-low)]",
      },
      size: {
        sm: "h-[18px] px-1.5 text-[10px]",
        md: "h-[22px] px-2 text-xs",
      },
    },
  },
);

const dotClass: Record<RiskLevel, string> = {
  high: "bg-[var(--risk-high)]",
  med: "bg-[var(--risk-med-ink)]",
  low: "bg-[var(--risk-low)]",
};

// RiskTag levels map onto the shared results:riskLevels.* label keys.
const levelLabelKey: Record<RiskLevel, string> = {
  high: "HIGH",
  med: "MEDIUM",
  low: "LOW",
};

type RiskLevel = "high" | "med" | "low";

interface RiskTagProps
  extends
    Omit<HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof riskTagVariants> {
  level?: RiskLevel;
  size?: "sm" | "md";
  /** Override the translated default label (e.g. a pre-formatted string). */
  label?: ReactNode;
}

export function RiskTag(
  { level = "high", size = "md", label, className, ...props }: RiskTagProps,
) {
  const { t } = useTranslation(["results"]);
  const text = label ?? t(`results:riskLevels.${levelLabelKey[level]}`);

  return (
    <span
      data-slot="risk-tag"
      className={cn(riskTagVariants({ level, size }), className)}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn("size-1.5 shrink-0 rounded-full", dotClass[level])}
      />
      {text}
    </span>
  );
}
